import { forwardRef } from 'react';

import { twMerge } from 'tailwind-merge';

import circleSvg from './circle.svg';
import Styles from './styles.module.css';

type Ref = HTMLInputElement;

const SIZE_STYLE = {
  sm: 'h-5 w-5 checked:border',
  md: 'h-6 w-6 checked:border bg-10',
  lg: 'h-8 w-8 checked:border-2 bg-13',
};

const radioClasses = twMerge(
  'peer h-4 w-4 shrink-0 cursor-pointer border-neutral-300 bg-white',
  'checked:border-blue-400 checked:text-blue-400 disabled:cursor-not-allowed',
  'dark:border-neutral-700 dark:bg-neutral-700',
  'dark:checked:border-blue-400',
  'dark:ring-offset-neutral-900 dark:focus:ring-blue-300',
);

const orientations = {
  horizontal: 'flex-row',
  vertical: 'flex-col',
};

interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly orientation?: keyof typeof orientations;
  readonly size?: 'sm' | 'md' | 'lg';
}

function RadioGroup({ children, orientation = 'horizontal', className, ...rest }: RadioGroupProps) {
  return (
    <div {...rest} className={twMerge('flex flex-col gap-2', orientations[orientation], className)}>
      {children}
    </div>
  );
}

type RadioDefaultProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  readonly size?: 'sm' | 'md' | 'lg';
};

const RadioDefault = forwardRef<Ref, RadioDefaultProps>(function RadioDefault(props, ref) {
  const { className, children, size = 'sm', ...rest } = props;

  return (
    <div className={twMerge('flex items-center', className)}>
      <input
        type="radio"
        ref={ref}
        style={{ '--image-url': `url(${circleSvg})` } as React.CSSProperties}
        // style={{ backgroundImage: `url(${circleSvg})` }}
        className={twMerge(
          Styles[`custom-input-radio-${size}`],
          SIZE_STYLE[size],
          'peer shrink-0 cursor-pointer border-neutral-300 !bg-white',
          'checked:text-blue-400 disabled:cursor-not-allowed',
          'dark:border-neutral-700 dark:bg-neutral-700',
          'dark:checked:border-blue-400',
          'dark:ring-offset-neutral-900 dark:focus:ring-blue-300',
          'focus-ring focus:ring-offset-0',
          'checked:border-brand checked:bg-[image:var(--image-url)]',
        )}
        {...rest}
      />
      {children && (
        <label
          htmlFor={rest.id}
          data-testid="radio"
          className={twMerge(
            'ml-3 inline-flex cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:text-neutral-400 dark:text-neutral-300',
          )}
        >
          {children}
        </label>
      )}
    </div>
  );
});

const RadioCard = forwardRef<Ref, React.InputHTMLAttributes<HTMLInputElement>>(function RadioCard(props, ref) {
  const { className, children, ...rest } = props;

  return (
    <div className={twMerge('relative flex flex-1', className)}>
      <input ref={ref} type="radio" className={twMerge(radioClasses, 'absolute left-2 top-2')} {...rest} />
      <label
        htmlFor={rest.id}
        data-testid="radio-card"
        className={twMerge(
          'peer:border-blue-400 h-full w-full flex-1 cursor-pointer rounded-md border border-neutral-300 p-6 peer-checked:border peer-checked:border-blue-400 peer-checked:ring-1 peer-checked:ring-blue-400 dark:border-neutral-700',
        )}
      >
        {children}
      </label>
    </div>
  );
});

const Radio = RadioDefault as typeof RadioDefault & {
  Group: typeof RadioGroup;
  Card: typeof RadioCard;
};

Radio.Group = RadioGroup;
Radio.Card = RadioCard;

export { Radio };
